// -----------------------------
// CustomerDashboardLayout.tsx
// -----------------------------
"use client";

import React, {
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
  Suspense,
} from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  Book,
  Home,
  ShoppingBag,
  Star,
  Wallet,
} from "lucide-react";

import ModernDashboardLayout from "@/components/DashboardLayout";
import { NavItem, SidebarItem } from "@/components/dashboardComponents";
import { useRoleSession } from "@/hooks/UseRoleSession";
import { useSession } from "@/providers/SessionProvider";
import { useMultiSessionContext } from "@/providers/MultiSessionProvider";
import { useSearchParams, useRouter } from "next/navigation"; 
import { Cart, Customer, MenuItem } from "@/types/inde_menu";
import PaymentProvider from "@/providers/PaymentProvider";
import { usePaymentRequestStore } from "@/stores/usePaymentRequestStore";
import SplitPaymentModal from "@/components/SplitPaymentModal";

export interface CustomerDashboardLayoutProps {
  user?: Customer | null;
  cart?: Cart | null;
  children: ReactNode;
  cartItemCount?: number;
  onCartClick?: (item: MenuItem) => void;
  allowGuest?: boolean;
}

function SearchParamsHandler({
  setUserId,
  setGuestName,
}: {
  setUserId: (id: string | null) => void;
  setGuestName: (name: string) => void;
}) {
  const searchParams = useSearchParams();
  useEffect(() => {
    setUserId(searchParams.get("userid"));
    const name = searchParams.get("name");
    if (name) setGuestName(name);
  }, [searchParams, setUserId, setGuestName]);
  return null;
}

export default function CustomerDashboardLayout({
  onCartClick,
  cartItemCount,
  user,
  allowGuest = false,
  children,
}: CustomerDashboardLayoutProps) {
  const [userIdFromUrl, setUserIdFromUrl] = useState<string | null>(null);
  const [guestNameFromUrl, setGuestNameFromUrl] = useState<string>("Guest Customer");
 
  const [notifications, setNotifications] = useState(2);
  const initializationAttempted = useRef(false);

  const {
    user: customerUser,
    sessionId,
    switchToThisRole,
    createNewSessionForRole,
    refreshProfile,
    isLoading: sessionLoading,
    hasSession,
    isCurrentRole,
  } = useRoleSession<Customer>({ role: "customer", autoSwitch: !allowGuest });
 
  const { invalidate } = useSession();
  const { createNewRole } = useMultiSessionContext();

  const router = useRouter();
  const { showModal } = usePaymentRequestStore();

  // Strict Role Check for Static Exports
  useEffect(() => {
    if (userIdFromUrl || allowGuest) return; // Special flow & Guest flow
    
    if (!sessionLoading && !hasSession) {
      toast.error("Please log in to continue.");
      router.push("/login");
    }
  }, [hasSession, sessionLoading, userIdFromUrl, allowGuest, router]);

  // Notifications persistence
  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("customer-notifications");
      setNotifications(saved ? parseInt(saved, 10) : 2);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined")
      localStorage.setItem("customer-notifications", notifications.toString());
  }, [notifications]);

  const navItems: NavItem[] = useMemo(
    () => [
      {
        label: "Home",
        link: "/dashboards/customer",
        icon: <Home className="h-5 w-5" />,
        showOn: ["customer"],
      },
      {
        label: "My Orders",
        link: "/dashboards/customer/orders",
        icon: <ShoppingBag className="h-5 w-5" />,
        showOn: ["customer"],
      },
      {
        label: "Bookings",
        link: "/dashboards/customer/bookings",
        icon: <Book className="h-5 w-5" />,
        showOn: ["customer"],
      },
    ],
    []
  );

  const sidebarItems: SidebarItem[] = useMemo(
    () => [
      ...navItems,
      {
        label: "Loyalty",
        link: "/dashboards/customer/loyalty",
        icon: <Star className="h-5 w-5" />,
        showOn: ["customer"],
      },
      {
        label: "Inde Pay",
        link: "/dashboards/customer/inde_pay",
        icon: <Wallet className="h-5 w-5" />,
        showOn: ["customer"],
      },
    ],
    [navItems]
  );

  const guestCustomer: Customer = useMemo(() => ({
    id: "guest-session",
    name: guestNameFromUrl,
    full_name: guestNameFromUrl,
    email: "guest@indemenu.mw",
    phone: "",
    role: "customer",
    address: { city: "Lilongwe", country: "Malawi" },
  }), [guestNameFromUrl]);

  const effectiveUser =  customerUser || user || (allowGuest ? guestCustomer : null);

  const firstName = useMemo(
    () => effectiveUser?.name.split(" ")[0] ?? "Guest",
    [effectiveUser]
  );

  const isLoading =
    (sessionLoading && !allowGuest && (!hasSession && !userIdFromUrl)) || !effectiveUser;

  const handleLogout = useCallback(async () => {
    if (!sessionId) return;
    try {
      invalidate('user_logout');
      toast.success("Logged out successfully");
    } catch {
      toast.error("Failed to log out");
    }
  }, [sessionId, invalidate]);

  const handleRefreshProfile = useCallback(async () => {
    try {
      await refreshProfile();
      toast.success("Profile refreshed");
    } catch {
      toast.error("Failed to refresh profile");
    }
  }, [refreshProfile]);

  const handleNotificationClick = useCallback(
    () => setNotifications((prev) => prev + 1),
    []
  );

  // Loading fallback
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-md w-full text-center shadow-lg rounded-xl backdrop-blur-md bg-white/90 border border-blue-200 p-8">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-12 w-12 bg-blue-200 rounded-full mb-4"></div>
            <div className="h-6 w-48 bg-blue-200 rounded mb-4"></div>
            <div className="h-4 w-32 bg-blue-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!effectiveUser) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-md w-full text-center shadow-lg rounded-xl backdrop-blur-md bg-white/90 border border-blue-200 p-8">
          <div className="flex items-center justify-center gap-3 text-blue-600 mb-6">
            <AlertTriangle className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Access Restricted</h2>
          </div>
          <p className="text-gray-700 mb-6">
            You need customer privileges to access this dashboard.
          </p>
          <button
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md transition-all duration-200"
            onClick={async () => {
              try {
                await createNewRole("vendor");
                toast.success("Vendor session created");
              } catch {
                toast.error("Failed to create vendor session");
              }
            }}
          >
            Access as Vendor
          </button>
        </div>
      </div>
    );
  }

  return (
    <PaymentProvider>
      <ModernDashboardLayout
        navItems={navItems}
        sidebarItems={sidebarItems}
        headerTitle={`Welcome, ${firstName}`}
        targetRole={
          effectiveUser?.role as "vendor" | "customer" | "waiter" | "admin"
        }
        user={effectiveUser as Customer}
        notificationsCount={notifications}
        cartItemCount={cartItemCount}
        onLogout={handleLogout}
        onNotificationClick={handleNotificationClick}
        onProfileClick={handleRefreshProfile}
        onCartClick={onCartClick}
        profileHref="/dashboards/customer/profile"
      >
        {" "}
        {children}
        {/* SplitPaymentModal rendered outside provider logic for proper overlay */}
        {showModal && <SplitPaymentModal />}
      </ModernDashboardLayout>
      <Suspense fallback={null}>
        <SearchParamsHandler setUserId={setUserIdFromUrl} setGuestName={setGuestNameFromUrl} />
      </Suspense>
    </PaymentProvider>
  );
}
